import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import LockOutlined from '@mui/icons-material/LockOutlined';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import NotInterested from '@mui/icons-material/NotInterested';
import LocalMallOutlined from '@mui/icons-material/LocalMallOutlined';
import StoreOutlined from '@mui/icons-material/StoreOutlined';
import DirectionsCarOutlined from '@mui/icons-material/DirectionsCarOutlined';
import HotelOutlined from '@mui/icons-material/HotelOutlined';
import Inventory2Outlined from '@mui/icons-material/Inventory2Outlined';
import LocalShippingOutlined from '@mui/icons-material/LocalShippingOutlined';
import ChatBubbleOutline from '@mui/icons-material/ChatBubbleOutline';
import ShoppingBagOutlined from '@mui/icons-material/ShoppingBagOutlined';
import { COUNTER_KEY, CURBSIDE_KEY, BEDSIDE_KEY, COURIER_KEY, SHOP_KEY, OTC_DELIVERY_KEY, CUSTOM_BRAND_MESSAGING_KEY, isFeatureUsable } from '../../utils/productLicensing';
import './features.css';

const iconButtonSx = {
  padding: '4px',
  color: '#475569',
};

export const FeatureGlyph = ({ featureKey, className }) => {
  if (featureKey === OTC_DELIVERY_KEY) {
    return <LocalMallOutlined className={className} />;
  }
  if (featureKey === COUNTER_KEY) {
    return <StoreOutlined className={className} />;
  }
  if (featureKey === CURBSIDE_KEY) {
    return <DirectionsCarOutlined className={className} />;
  }
  if (featureKey === BEDSIDE_KEY) {
    return <HotelOutlined className={className} />;
  }
  if (featureKey === SHOP_KEY) {
    return <ShoppingBagOutlined className={className} />;
  }
  if (featureKey === COURIER_KEY) {
    return <LocalShippingOutlined className={className} />;
  }
  if (featureKey === CUSTOM_BRAND_MESSAGING_KEY) {
    return <ChatBubbleOutline className={className} />;
  }
  return <Inventory2Outlined className={className} />;
};

FeatureGlyph.propTypes = {
  featureKey: PropTypes.string,
  className: PropTypes.string,
};

export const FeatureStatusIcons = ({
  entitlement,
  t,
  onInfoViewed,
  size = 'small',
}) => {
  const [infoOpen, setInfoOpen] = useState(false);
  if (!entitlement) {
    return null;
  }
  const geoBlocked = entitlement.reason === 'geo_blocked';
  const enabled = isFeatureUsable(entitlement);
  const infoCopy = geoBlocked
    ? t('Features.TooltipGeo', {
        defaultValue: 'Not available at this location because of local rules.',
      })
    : enabled
      ? entitlement.tooltip
      : `${entitlement.tooltip || ''} ${t('Features.TooltipTalk', {
          defaultValue: 'Talk to an expert to enable.',
        })}`.trim();
  const infoTitle =
    entitlement.title ||
    t('Features.InfoAria', { defaultValue: 'Feature details' });

  const openInfo = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setInfoOpen(true);
    if (onInfoViewed) {
      onInfoViewed();
    }
  };

  const closeInfo = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setInfoOpen(false);
  };

  return (
    <span className="feature-status-icons">
      {geoBlocked ? (
        <Tooltip title={t('Features.IconGeo', {
          defaultValue: 'Not available at this location because of local rules.',
        })}>
          <NotInterested fontSize={size} className="feature-icon feature-icon-ban" />
        </Tooltip>
      ) : enabled ? (
        <Tooltip title={t('Features.IconEnabled', {
          defaultValue: 'Enabled',
        })}>
          <CheckCircleOutline fontSize={size} className="feature-icon feature-icon-check" />
        </Tooltip>
      ) : (
        <Tooltip title={t('Features.IconLocked', {
          defaultValue: 'Not enabled. Open to learn more or talk to an expert.',
        })}>
          <LockOutlined fontSize={size} className="feature-icon feature-icon-lock" />
        </Tooltip>
      )}
      <IconButton
        size="small"
        sx={iconButtonSx}
        aria-label={t('Features.InfoAria', { defaultValue: 'Feature details' })}
        onClick={openInfo}
      >
        <InfoOutlined fontSize={size} className="feature-icon feature-icon-info" />
      </IconButton>
      <Dialog
        open={infoOpen}
        onClose={closeInfo}
        maxWidth="sm"
        fullWidth
        onClick={(event) => event.stopPropagation()}
      >
        <DialogTitle sx={{ fontWeight: 700, pr: 2 }}>
          {infoTitle}
        </DialogTitle>
        <DialogContent>
          <p className="feature-info-dialog-copy">{infoCopy}</p>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={closeInfo}
            variant="contained"
            sx={{
              textTransform: 'none',
              backgroundColor: '#00a7e1',
              borderRadius: '10px',
              fontWeight: 600,
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#0096cc' },
            }}
          >
            {t('Common.Close', { defaultValue: 'Close' })}
          </Button>
        </DialogActions>
      </Dialog>
    </span>
  );
};

FeatureStatusIcons.propTypes = {
  entitlement: PropTypes.object,
  t: PropTypes.func.isRequired,
  onInfoViewed: PropTypes.func,
  size: PropTypes.string,
};



// WEBPACK FOOTER //
// ./src/components/features/FeatureMarkers.js